import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { secureGet, securePost } from '../../config/api';
import { formatDate } from '../../utils/format';
import Input from '../../components/Input';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';
import { PrimaryButton, SaveCancel } from '../../components/PrimaryButton';
import AddIcon from '../../icons/plusB.svg';

const Subscription = () => {
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [extraSeatsPopupOpen, setExtraSeatsPopupOpen] = useState(false);
  const [extraSeats, setExtraSeats] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const grayClientId = useMemo(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('settings') || '{}');
      return settings.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    if (!grayClientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await secureGet(`/subscriptions/by-grayClient/${grayClientId}`, showSnackbar, { signal });
      setSubscription(res?.data ?? null);
      console.log(res?.data);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setSubscription(null);
      }
    } finally {
      setLoading(false);
    }
  }, [grayClientId, signal]);

  useEffect(() => {
    fetchSubscription();
    return () => abortController.abort();
  }, [fetchSubscription, abortController]);

  const handleOpenExtraSeatsPopup = () => {
    setExtraSeats('');
    setExtraSeatsPopupOpen(true);
  };

  const handleCloseExtraSeatsPopup = () => {
    setExtraSeatsPopupOpen(false);
    setExtraSeats('');
  };

  const handleSubmitExtraSeats = async () => {
    const num = parseInt(extraSeats, 10);
    if (Number.isNaN(num) || num < 1) {
      showSnackbar(t('subscription.extraSeatsInvalid') || 'Please enter a valid number of extra seats (at least 1).', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await securePost(
        `/users/requestextraseats`,
        { extraSeats: num },
        showSnackbar
      );
      showSnackbar(t('subscription.requestExtraSeatsSent'), 'success');
      handleCloseExtraSeatsPopup();
    } catch (err) {
      if (err?.name !== 'AbortError') {
        // Error already handled by checkNotAuthored / showSnackbar
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateOrNull = (v) => (v ? formatDate(v) : '');

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <LoadingLogo />
      </div>
    );
  }

  if (!grayClientId) {
    return (
      <div>
        <Helmet>
          <title>GrayFox - {t('titles.subscription')}</title>
        </Helmet>
        <h1 className="text-[40px] font-semibold mb-4">{t('titles.subscription')}</h1>
        <SnackbarNotification
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div>
        <Helmet>
          <title>GrayFox - {t('titles.subscription')}</title>
        </Helmet>
        <h1 className="text-[40px] font-semibold mb-4">{t('titles.subscription')}</h1>
        <SnackbarNotification
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>GrayFox - {t('titles.subscription')}</title>
      </Helmet>

      <div className="flex items-center justify-between mb-8">
        <h1 className="text-[40px] font-semibold">{t('titles.subscription')}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            icon={AddIcon}
            label={t('subscription.requestExtraSeats') || 'Request extra seats'}
            onClick={handleOpenExtraSeatsPopup}
          />
        </div>
      </div>


      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <Input label={t('subscription.planName')} value={t(`plans.${subscription.subscriptionPlanName}`) || ''} onChange={() => {}} disabled />
        <Input label={t('subscription.billingInterval')} value={t(`billingIntervals.${subscription.subscriptionBillingInterval}`) || ''} onChange={() => {}} disabled />
        <Input label={t('subscription.discount')} value={String(subscription.subscriptionDiscount ?? '')} onChange={() => {}} disabled />
        <Input label={t('subscription.currency')} value={subscription.subscriptionCurrency ?? ''} onChange={() => {}} disabled />
        <Input label={t('subscription.tva')} value={String(subscription.subscriptionTVA ?? '')} onChange={() => {}} disabled />
        <Input label={t('subscription.startDate')} value={formatDateOrNull(subscription.subscriptionStartDate)} onChange={() => {}} disabled />
        <Input label={t('subscription.currentPeriodStart')} value={formatDateOrNull(subscription.subscriptionCurrentPeriodStart)} onChange={() => {}} disabled />
        <Input label={t('subscription.currentPeriodEnd')} value={formatDateOrNull(subscription.currentPeriodEnd)} onChange={() => {}} disabled />
        <Input label={t('subscription.nextBillingAt')} value={formatDateOrNull(subscription.nextBillingAt)} onChange={() => {}} disabled />
        <Input label={t('subscription.includedSeats')} value={String(subscription.subscriptionIncludedSeats ?? '')} onChange={() => {}} disabled />
        <Input label={t('subscription.pricePerUser')} value={String(subscription.subscriptionPricePerUser ?? '')} onChange={() => {}} disabled />
      </div>

      {extraSeatsPopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
          <div className="relative w-[500px] bg-[#F9FAFC] shadow-[0px_20px_80px_#00000011] rounded-[12px] p-6">
            <h3 className="text-[20px] font-semibold text-[#38485C] mb-4">
              {t('subscription.requestExtraSeats') || 'Request extra seats'}
            </h3>
            <div className="mb-6">
              <Input
                label={t('subscription.extraSeats') || 'Extra seats'}
                type="text"
                value={extraSeats}
                onChange={setExtraSeats}
              />
            </div>
            <div className="flex justify-end">
              <SaveCancel
                onCancelClick={handleCloseExtraSeatsPopup}
                onSaveClick={handleSubmitExtraSeats}
              />
            </div>
            {submitting && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-[12px]">
                <LoadingLogo />
              </div>
            )}
          </div>
        </div>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default Subscription;
